'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface NavigationItem {
  label: string;
  anchor: string;
  description: string;
  icon: string;
}

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  const navigationItems: NavigationItem[] = [
    {
      label: 'Products',
      anchor: '/landing-page#products',
      description: 'Authentic pickles, murrabas & ghee',
      icon: 'ShoppingBagIcon',
    },
    {
      label: 'Our Story',
      anchor: '/landing-page#our-story',
      description: 'Heritage and traditional methods',
      icon: 'BookOpenIcon',
    },
    {
      label: 'Why Choose Us',
      anchor: '/landing-page#why-choose-us',
      description: 'Benefits and ingredient transparency',
      icon: 'CheckBadgeIcon',
    },
    {
      label: 'Reviews',
      anchor: '/landing-page#reviews',
      description: 'Customer testimonials',
      icon: 'StarIcon',
    },
  ];

  const moreMenuItems: NavigationItem[] = [
    {
      label: 'Recipes',
      anchor: '/landing-page#recipes',
      description: 'Traditional preparation ideas',
      icon: 'DocumentTextIcon',
    },
    {
      label: 'FAQ',
      anchor: '/landing-page#faq',
      description: 'Common questions answered',
      icon: 'QuestionMarkCircleIcon',
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      const sections = [
        'products',
        'our-story',
        'why-choose-us',
        'reviews',
        'recipes',
        'faq',
      ];
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(`#${section}`);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (anchor: string) => {
    setIsMenuOpen(false);
    
    // Extract hash fragment from full path (e.g., '/landing-page#products' -> '#products')
    const hash = anchor.includes('#') ? anchor.substring(anchor.indexOf('#')) : anchor;
    const element = document.querySelector(hash);
    
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <header
      className={`sticky top-0 z-[100] bg-card shadow-md transition-all duration-250 ease-out ${
        isScrolled ? 'shadow-lg' : ''
      } ${className}`}
    >
      <nav className="mx-auto max-w-[1200px] px-6">
        <div className="flex h-[60px] items-center justify-between">
          <Link
            href="/landing-page"
            className="flex items-center gap-3 transition-opacity duration-250 ease-out hover:opacity-80"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary overflow-hidden">
              <AppImage
                src="/assets/images/Grannyz_logo_wht-01-01-1766916994944.png"
                alt="Granny'z Farm Fresh Logo"
                width={40}
                height={40}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-heading text-xl font-bold leading-tight text-primary-foreground">
                Granny'z Farm Fresh
              </span>
              <span className="font-accent text-xs text-muted-foreground">
                Taste Tradition, Choose Health
              </span>
            </div>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {navigationItems.map((item) => (
              <button
                key={item.anchor}
                onClick={() => handleNavClick(item.anchor)}
                className={`group relative px-4 py-2 text-base font-medium transition-colors duration-250 ease-out ${
                  activeSection === item.anchor
                    ? 'text-primary' :'text-foreground hover:text-primary'
                }`}
                aria-current={activeSection === item.anchor ? 'page' : undefined}
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-0 h-0.5 w-full origin-left scale-x-0 bg-primary transition-transform duration-250 ease-out group-hover:scale-x-100 ${
                    activeSection === item.anchor ? 'scale-x-100' : ''
                  }`}
                />
              </button>
            ))}

            <div className="group relative">
              <button className="flex items-center gap-1 px-4 py-2 text-base font-medium text-foreground transition-colors duration-250 ease-out hover:text-primary">
                More
                <Icon name="ChevronDownIcon" size={16} />
              </button>
              <div className="absolute left-0 right-0 top-full h-2 hidden group-hover:block" />
              <div className="absolute right-0 top-full mt-2 hidden w-64 rounded-lg bg-popover shadow-cta group-hover:block">
                {moreMenuItems.map((item) => (
                  <button
                    key={item.anchor}
                    onClick={() => handleNavClick(item.anchor)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors duration-250 ease-out hover:bg-muted"
                  >
                    <Icon
                      name={item.icon as any}
                      size={20}
                      className="mt-0.5 text-primary"
                    />
                    <div>
                      <div className="font-medium text-popover-foreground">
                        {item.label}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {item.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center justify-center rounded-lg p-2 text-foreground transition-colors duration-250 ease-out hover:bg-muted lg:hidden"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <Icon name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-border py-4 lg:hidden">
            <div className="flex flex-col gap-1">
              {[...navigationItems, ...moreMenuItems].map((item) => (
                <button
                  key={item.anchor}
                  onClick={() => handleNavClick(item.anchor)}
                  className={`flex items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors duration-250 ease-out ${
                    activeSection === item.anchor
                      ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon
                    name={item.icon as any}
                    size={20}
                    className={
                      activeSection === item.anchor ? 'text-primary' : ''
                    }
                  />
                  <div className="flex-1">
                    <div className="font-medium">{item.label}</div>
                    <div className="text-sm text-muted-foreground">
                      {item.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;